/**
 * Карточка клиента во вкладке «Новый год» (29.10.2026, владелец: «одна карточка на клиента — все его НГ-брони и НГ-заявки,
 * скрины, заметки»). Свёрнута — имя, телефон, ближайшая игра, счётчики; раскрыта — брони (тап → сетка дня брони + модалка),
 * заявки без времени («Обработан» — штатный DELETE /api/lead-inbox/:id), «История», «Скрины» и «Инфо» (ClientInfoCard).
 */
import { useEffect, useRef, useState } from "react";
import { PlayersBadge } from "../../components/shared";
import { ClientInfoCard } from "../../components/modal/ClientInfoCard";
import { LeadDone } from "./DcLeadsSection";
import { fmtDM, fmtStamp, groupOf, infoTarget, nextBooking, plural, sourceLabel, weekday, NY_LEAD_DONE, type NyBooking, type NyCard, type NyLead } from "./nyModel";
import { NyNotes } from "./NyNotes";
import { NyShots } from "./NyShots";
import ny from "./dcNy.module.css";

const Chev = () => <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 4l4 4 4-4" /></svg>;
const hm = (b: NyBooking) => (b.custom_time || b.time || "").slice(0, 5);

function BookingRow({ b, name, onOpen }: { b: NyBooking; name: string; onOpen: (b: NyBooking) => void }) {
  return (
    <div className={`${ny.bk} ${b.type === "lead" ? ny.bkLead : ""}`} role="button" tabIndex={0} title="Открыть бронь в сетке"
      onClick={() => onOpen(b)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(b); } }}>
      <div className={ny.bkRow}>
        <span className={ny.bkWhen}>{fmtDM(b.date)} · {hm(b)}</span>
        <span className={ny.bkWd}>{weekday(b.date)}</span>
        <span className={ny.bkQuest}>{b.quest || "—"}</span>
        {/* канон 05.09: число игроков — только PlayersBadge; клик — карточка клиента по брони. Обёртка гасит всплытие:
            карточка клиента рисуется внутри строки, и тап по её фону (закрыть) иначе открыл бы бронь */}
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <PlayersBadge count={b.players} bookingId={b.id} fallbackName={name} size="sm" />
        </span>
        {b.type === "lead" ? <span className={ny.bkTag}>лид</span> : null}
      </div>
      {b.comment ? <div className={ny.bkNote}>{b.comment}</div> : null}
    </div>
  );
}

function LeadRow({ l, onChanged, onToast }: { l: NyLead; onChanged: () => void; onToast: (m: string) => void }) {
  const open = l.status === "new";
  return (
    <div className={`${ny.ld} ${open ? "" : ny.ldDone}`}>
      <div className={ny.ldHead}>
        <span className={ny.src} title={l.source ?? undefined}>{sourceLabel(l)}</span>
        <span>{fmtStamp(l.created_at)}</span>
        <span className={ny.ldState}>{open ? "новая" : l.status === "dismissed" ? "обработана" : l.status}</span>
      </div>
      {l.when || l.people ? (
        <div className={ny.ldWhen}>
          {l.when ? <span>Когда: <b>{l.when}</b></span> : null}
          {l.people ? <PlayersBadge count={l.people} size="sm" /> : null}
        </div>
      ) : null}
      {l.wishes ? <div className={ny.ldText}>{l.wishes}</div> : null}
      {l.manual && l.by ? <div className={ny.ldBy}>Внёс: {l.by}</div> : null}
      {open ? <LeadDone id={l.id} onChanged={onChanged} onToast={onToast} texts={NY_LEAD_DONE} /> : null}
    </div>
  );
}

type Panel = "notes" | "shots" | null;

export function NyCardView({ card, today, open, scrollTo, onToggle, onOpenBooking, onChanged, onToast }: {
  card: NyCard; today: string; open: boolean; scrollTo: boolean; onToggle: () => void;
  onOpenBooking: (b: NyBooking) => void; onChanged: () => void; onToast: (m: string) => void;
}) {
  const [panel, setPanel] = useState<Panel>(null);
  const [info, setInfo] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  /* возврат из модалки брони: раскрытая карточка — снова на экране */
  useEffect(() => { if (scrollTo) ref.current?.scrollIntoView({ block: "start" }); }, [scrollTo]);
  const name = card.name ?? "";
  const title = name || card.phone || "Без имени";
  const next = nextBooking(card, today);
  const hot = groupOf(card, today) === 0;
  const target = infoTarget(card);
  const noRef = card.id == null;
  const toggle = (p: Panel) => setPanel((x) => (x === p ? null : p));
  const counts = [
    card.bookings.length ? `${card.bookings.length} ${plural(card.bookings.length, "бронь", "брони", "броней")}` : "",
    card.leads.length ? `${card.leads.length} ${plural(card.leads.length, "заявка", "заявки", "заявок")}` : "",
    card.notes ? `заметок ${card.notes}` : "", card.shots ? `скринов ${card.shots}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <div ref={ref} className={`${ny.card} ${hot ? ny.cardHot : ""} ${open ? ny.cardOpen : ""}`}>
      <button type="button" className={ny.head} onClick={onToggle} aria-expanded={open}>
        <span className={ny.nameRow}>
          <span className={`${ny.name} ${name ? "" : ny.nameDim}`}>{title}</span>
          {hot ? <span className={ny.tagNew}>заявка</span> : null}
        </span>
        <span className={`${ny.chev} ${open ? ny.chevOpen : ""}`}><Chev /></span>
        <span className={ny.meta}>
          {name && card.phone ? <span className={ny.phone}>{card.phone}</span> : null}
          {next ? <span className={ny.next}>{fmtDM(next.date)} · {hm(next)} · {next.quest}</span> : null}
          {counts ? <span className={ny.cnt}>{counts}</span> : null}
        </span>
      </button>
      {open ? (
        <div className={ny.body}>
          {card.phone ? (
            <a className={ny.call} href={`tel:${card.phone.replace(/[^\d+]/g, "")}`}>
              <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 3a1 1 0 0 1 1-1h1.5l1 2-1 1a6 6 0 0 0 3 3l1-1 2 1V9.5a1 1 0 0 1-1 1A9 9 0 0 1 2 3z" /></svg>
              {card.phone}
            </a>
          ) : null}
          {card.bookings.length ? <div className={ny.lbl}>Брони и лиды со слотом<b>{card.bookings.length}</b></div> : null}
          {card.bookings.map((b) => <BookingRow key={b.id} b={b} name={name} onOpen={onOpenBooking} />)}
          {card.leads.length ? <div className={ny.lbl}>Заявки без времени<b>{card.leads.length}</b></div> : null}
          {card.leads.map((l) => <LeadRow key={l.id} l={l} onChanged={onChanged} onToast={onToast} />)}
          <div className={ny.acts}>
            <button type="button" className={`${ny.act} ${panel === "notes" ? ny.actOn : ""}`} disabled={noRef} onClick={() => toggle("notes")}>История{card.notes ? <b>{card.notes}</b> : null}</button>
            <button type="button" className={`${ny.act} ${panel === "shots" ? ny.actOn : ""}`} disabled={noRef} onClick={() => toggle("shots")}>Скрины{card.shots ? <b>{card.shots}</b> : null}</button>
            <button type="button" className={ny.act} disabled={!target} onClick={() => setInfo(true)} title="История игр клиента">Инфо</button>
          </div>
          {noRef ? <div className={ny.hint}>Нет телефона клиента (или это номер-заглушка) — историю и скрины привязать не к кому.</div> : null}
          {card.id != null && panel === "notes" ? <NyNotes cardRef={card.id} onChanged={onChanged} onToast={onToast} /> : null}
          {card.id != null && panel === "shots" ? <NyShots cardRef={card.id} onChanged={onChanged} onToast={onToast} /> : null}
        </div>
      ) : null}
      {info && target ? (
        <ClientInfoCard {...("bookingId" in target ? { bookingId: target.bookingId } : { phone: target.phone })}
          fallbackName={name || undefined} onClose={() => setInfo(false)} />
      ) : null}
    </div>
  );
}
